import type { ProjectItem } from '@store/reducers/dashboard/types'
import type React from 'react'
import { useState } from 'react'

import { clsJoin } from '@/utils'
import { ReportFavicon } from '../../../ReportFavicon'
import { PreviewSkeleton } from '../PreviewSkeleton'
import css from './styles.module.css'

interface Props {
  project: ProjectItem
}

// A report project has no canvas snapshot to screenshot, so its card shows
// the crawled site's favicon instead of the usual sitemap preview.
const ReportThumbnail: React.FC<{ faviconUrl?: string | null }> = ({
  faviconUrl,
}) => (
  <div className={clsJoin(css.project_thumbnail, css.report_thumbnail)}>
    <ReportFavicon faviconUrl={faviconUrl} className={css.favicon} />
  </div>
)

const SitemapThumbnail: React.FC<{
  thumbnail: string | null
  title: string
}> = ({ thumbnail, title }) => {
  // The stored preview can outlive its file in the bucket, and then the card
  // shows the browser's broken-image icon — which reads as a broken project.
  // Remembering the url that failed, rather than a flag, lets the card render
  // again once the thumbnail cron replaces it with a new one.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const missing = !thumbnail || thumbnail === failedUrl

  return (
    <div className={css.project_thumbnail}>
      {missing ? (
        <PreviewSkeleton />
      ) : (
        <img
          alt={`preview ${title}`}
          className={css.image}
          src={thumbnail}
          onError={() => setFailedUrl(thumbnail)}
        />
      )}
    </div>
  )
}

export const ProjectThumbnail: React.FC<Props> = ({ project }) => {
  const { thumbnail, title, type, faviconUrl } = project

  if (type === 'report') {
    return <ReportThumbnail faviconUrl={faviconUrl} />
  }

  return <SitemapThumbnail thumbnail={thumbnail} title={title} />
}
